// Per-browser user identifier. Without auth, the most stable thing we have
// is a UUID minted on first visit and persisted to localStorage. Sent as an
// X-TIA-User-Id header on every API call so the server can rate-limit per
// user instead of per IP.
//
// Caveats: clearing site data resets the ID (and the user's quota). The
// server still falls back to the IP if the header is absent, which is the
// case before the SDK boots or during certain incognito sessions. When auth
// lands, swap this for the real user identifier.

const STORAGE_KEY = 'tia.userId';

function generate(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `usr_${crypto.randomUUID()}`;
  }
  // Fallback for very old browsers
  return `usr_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function getUserId(): string {
  if (typeof window === 'undefined') return 'usr_ssr';
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing && /^usr_/.test(existing)) return existing;
    const fresh = generate();
    localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // localStorage disabled / private mode — return a fresh per-session ID.
    return generate();
  }
}

/** One-way short hash used to tag library entries with their author. Must
 *  stay byte-identical to the server-side ownerHash() in
 *  netlify/functions/_lib/store.ts so client-side "is this mine?" checks
 *  match server-side filtering. */
export function ownerHash(userId: string): string {
  let h = 5381;
  for (let i = 0; i < userId.length; i++) {
    h = ((h << 5) + h) ^ userId.charCodeAt(i);
  }
  return ((h >>> 0).toString(36).padStart(7, '0')).slice(0, 10);
}
